import * as React from "react";
import {
  HTMLTable,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  Dialog,
  FormGroup,
  InputGroup,
  Tag,
  Switch,
  type OverlayToaster,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin, { IChartProperty } from "../../../api/admin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useOutletContext } from "react-router-dom";
import { useAuthState } from "../../../context/auth";
import { isFybrAdminUser } from "../adminRoles";
import { useInternalCloudAdminGate } from "../useInternalCloudAdminGate";
import { stickyTableHeaderCss } from "../common/adminCss";

function isChartPropertyDeactivated(cp: IChartProperty): boolean {
  return cp.deleted_at != null && cp.deleted_at !== undefined;
}

function ChartPropertyEditDialog(props: {
  editProperty: IChartProperty | null;
  isCreate: boolean;
  onClose: () => void;
}) {
  const { editProperty, isCreate, onClose } = props;
  const [editErrors, setEditErrors] = React.useState<string[]>([]);
  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();
  const { currentUser } = useAuthState();
  const isFybrAdmin = isFybrAdminUser(currentUser);

  const createMutation = useMutation({
    mutationFn: ({ label }: { label: string }) => {
      if (!isFybrAdmin) {
        return Promise.reject(new Error("Only Fybr admins can create"));
      }
      return ApiAdmin.createChartProperty({ label });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chartProperties"] });
      toaster.current?.show({
        message: "Chart property created.",
        intent: "success",
        timeout: 3000,
      });
      onClose();
    },
    onError: (error: Error) => {
      toaster.current?.show({
        message: error.message || "Failed to create.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) => {
      if (!isFybrAdmin) {
        return Promise.reject(new Error("Only Fybr admins can update"));
      }
      return ApiAdmin.updateChartProperty(id, { label });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chartProperties"] });
      toaster.current?.show({
        message: "Chart property updated.",
        intent: "success",
        timeout: 3000,
      });
      onClose();
    },
    onError: (error: Error) => {
      toaster.current?.show({
        message: error.message || "Failed to update.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: string[] = [];
    const labelInput = event.currentTarget.elements.namedItem(
      "cp-label"
    ) as HTMLInputElement;
    const label = labelInput?.value?.trim() || "";
    if (!label) errors.push("label");
    if (errors.length) {
      setEditErrors(errors);
      return;
    }
    if (isCreate) {
      createMutation.mutate({ label });
    } else if (editProperty) {
      updateMutation.mutate({ id: editProperty.id, label });
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 480px;
      `}
      title={isCreate ? "Create Chart Property" : "Edit Chart Property"}
      isOpen={editProperty !== null || isCreate}
      onClose={() => {
        if (!pending) onClose();
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Label"
            labelFor="cp-label"
            labelInfo="(required)"
            intent={editErrors.includes("label") ? "danger" : "none"}
          >
            <InputGroup
              id="cp-label"
              name="cp-label"
              defaultValue={editProperty?.label || ""}
              disabled={pending}
              intent={editErrors.includes("label") ? "danger" : "none"}
              autoFocus
            />
          </FormGroup>
        </div>
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          `}
        >
          <Button
            text="Cancel"
            disabled={pending}
            intent="none"
            onClick={() => !pending && onClose()}
          />
          <Button
            text={pending ? "Saving..." : isCreate ? "Create" : "Save"}
            disabled={pending}
            intent="primary"
            type="submit"
          />
        </div>
      </form>
    </Dialog>
  );
}

export default function AdminInternalChartProperties() {
  const gate = useInternalCloudAdminGate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuthState();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const isFybrAdmin = isFybrAdminUser(currentUser);

  const [editDialog, setEditDialog] = React.useState<IChartProperty | null>(
    null
  );
  const [deleteDialog, setDeleteDialog] = React.useState<IChartProperty | null>(
    null
  );
  const [addOpen, setAddOpen] = React.useState(false);
  const [filterText, setFilterText] = React.useState("");
  const [showDeactivated, setShowDeactivated] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["chartProperties"],
    queryFn: () => ApiAdmin.getChartProperties(true),
    enabled: gate === "allowed",
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can deactivate chart properties")
        );
      }
      return ApiAdmin.deactivateChartProperty(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chartProperties"] });
      toaster.current?.show({
        message: "Chart property deactivated.",
        intent: "success",
        timeout: 3000,
      });
      setDeleteDialog(null);
    },
    onError: (err: Error) => {
      toaster.current?.show({
        message: err.message || "Failed to deactivate.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can activate chart properties")
        );
      }
      return ApiAdmin.activateChartProperty(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chartProperties"] });
      toaster.current?.show({
        message: "Chart property activated.",
        intent: "success",
        timeout: 3000,
      });
    },
    onError: (err: Error) => {
      toaster.current?.show({
        message: err.message || "Failed to activate.",
        intent: "danger",
        timeout: 5000,
      });
    },
  });

  const filtered = React.useMemo(() => {
    let list =
      data?.chart_properties.filter((cp) => {
        if (!showDeactivated && isChartPropertyDeactivated(cp)) return false;
        if (!filterText.trim()) return true;
        const q = filterText.toLowerCase();
        return cp.label.toLowerCase().includes(q);
      }) || [];
    return list.sort((a, b) => {
      const aA = !isChartPropertyDeactivated(a);
      const bA = !isChartPropertyDeactivated(b);
      if (aA !== bA) return aA ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [data?.chart_properties, filterText, showDeactivated]);

  if (gate !== "allowed") return null;

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  } else if (error) {
    content = (
      <NonIdealState
        icon="error"
        title="Error"
        description={(error as Error).message}
      />
    );
  } else if (
    filtered.length === 0 &&
    (data?.chart_properties.length || 0) > 0 &&
    !showDeactivated
  ) {
    const n =
      data?.chart_properties.filter(isChartPropertyDeactivated).length || 0;
    content = (
      <NonIdealState
        icon="chart"
        title="No Active Chart Properties"
        description={`All ${n} entr${n === 1 ? "y is" : "ies are"} deactivated. Toggle “Show deactivated” to view them.`}
        action={
          <Button
            icon="eye-open"
            text="Show deactivated"
            intent="primary"
            onClick={() => setShowDeactivated(true)}
          />
        }
      />
    );
  } else if (!data?.chart_properties.length) {
    content = (
      <NonIdealState
        icon="chart"
        title="No Chart Properties"
        description="Add a chart property to get started."
        action={
          isFybrAdmin ? (
            <Button
              icon="plus"
              text="Add"
              intent="primary"
              onClick={() => setAddOpen(true)}
            />
          ) : undefined
        }
      />
    );
  } else if (filtered.length === 0 && filterText.trim()) {
    content = (
      <NonIdealState
        icon="search"
        title="No matches"
        description={`Nothing matches “${filterText}”.`}
      />
    );
  } else {
    content = (
      <HTMLTable
        striped
        interactive
        css={[
          stickyTableHeaderCss,
          css`
            width: 100%;
          `,
        ]}
      >
        <colgroup>
          <col />
          <col width="128px" />
          <col width="64px" />
        </colgroup>
        <thead>
          <tr>
            <th>Label</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filtered.map((cp) => (
            <tr key={cp.id}>
              <td>
                <div
                  css={css`
                    padding-left: 5px;
                    color: ${isChartPropertyDeactivated(cp)
                      ? "var(--neutral-3)"
                      : "inherit"};
                  `}
                >
                  {cp.label}
                </div>
              </td>
              <td>
                <Tag
                  intent={
                    isChartPropertyDeactivated(cp) ? "warning" : "success"
                  }
                  minimal
                >
                  {isChartPropertyDeactivated(cp) ? "Deactivated" : "Active"}
                </Tag>
              </td>
              <td>
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                  `}
                >
                  <Popover
                    content={
                      <Menu>
                        {isFybrAdmin && !isChartPropertyDeactivated(cp) && (
                          <MenuItem
                            icon="edit"
                            text="Edit"
                            onClick={() => setEditDialog(cp)}
                          />
                        )}
                        {isFybrAdmin &&
                          (isChartPropertyDeactivated(cp) ? (
                            <MenuItem
                              icon="tick"
                              text="Activate"
                              intent="success"
                              onClick={() => activateMutation.mutate(cp.id)}
                            />
                          ) : (
                            <MenuItem
                              icon="disable"
                              text="Deactivate"
                              intent="danger"
                              onClick={() => setDeleteDialog(cp)}
                            />
                          ))}
                      </Menu>
                    }
                    placement="bottom-end"
                  >
                    <Button size="small" icon="more" />
                  </Popover>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </HTMLTable>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
      `}
    >
      {!isLoading && !error && (
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 1rem;
              flex: 1;
              padding-right: 1rem;
            `}
          >
            <InputGroup
              leftIcon="search"
              placeholder="Filter chart properties…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              css={css`
                width: 300px;
              `}
            />
            <Switch
              css={css`
                margin: 0;
              `}
              label="Show deactivated"
              checked={showDeactivated}
              onChange={(e) => setShowDeactivated(e.currentTarget.checked)}
            />
          </div>
          {isFybrAdmin && (
            <Button
              icon="plus"
              text="Add Chart Property"
              intent="primary"
              onClick={() => setAddOpen(true)}
            />
          )}
        </div>
      )}
      <div
        css={css`
          flex: 1;
          min-height: 0;
          overflow: auto;
        `}
      >
        {content}
      </div>
      <ChartPropertyEditDialog
        key={editDialog ? `e-${editDialog.id}` : addOpen ? "c" : "x"}
        editProperty={addOpen ? null : editDialog}
        isCreate={addOpen}
        onClose={() => {
          setEditDialog(null);
          setAddOpen(false);
        }}
      />
      <ConfirmationDialog
        entityName={deleteDialog?.label || ""}
        entityType="Chart Property"
        message="Deactivated properties can still be reactivated later."
        isOpen={deleteDialog !== null}
        onClose={() => setDeleteDialog(null)}
        onConfirm={() => deleteDialog && deleteMutation.mutate(deleteDialog.id)}
        isPending={deleteMutation.isPending}
        titleAction="Deactivate"
        actionText="Deactivate"
        actioningText="Deactivating…"
        icon="disable"
      />
    </div>
  );
}
